import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SaleOrderItemResponseDto } from '@bandido/shared/dtos';

import { Chip, Title } from '@frontend/features/sale-orders/styles';
import { ActiveOperationChip } from '@frontend/features/travel-services/components/ActiveOperationStatus';
import { useActiveOperationRecheck } from '@frontend/features/travel-services/activeOperationRecheck';
import { bookingStateColor } from '@frontend/features/travel-services/bookingState';
import { useTravelBookingByItem } from '@frontend/features/travel-services/queries';

import {
  getRuteroSaleOrderSummaryPresentation,
  RUTERO_SALE_ORDER_BOOKING_STATE_COLORS,
} from '../ruteroSaleOrderSummaryItem.helpers';

interface Props {
  item: SaleOrderItemResponseDto;
  isCompact: boolean;
}

export function RuteroSaleOrderSummaryItem({ item, isCompact }: Props) {
  const { t } = useTranslation();
  const { data: booking, isLoading, isError } = useTravelBookingByItem(item.id);

  useActiveOperationRecheck(booking);

  const presentation = getRuteroSaleOrderSummaryPresentation({
    booking,
    isLoading,
    isError,
  });
  const vertical =
    presentation.kind === 'booking' ? presentation.booking.vertical : item.type;

  return (
    <Stack spacing={0.5} minWidth={0} width="100%">
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        <Title
          variant={isCompact ? 'body2' : 'body1'}
          color={
            presentation.kind === 'booking' &&
            presentation.booking.state === 'cancelled'
              ? 'error.main'
              : 'text.primary'
          }
          style={{ flex: 1, minWidth: 0 }}
        >
          {t(`rutero.verticals.${vertical}`)}
        </Title>

        {presentation.kind === 'loading' && (
          <Chip
            label={t(presentation.messageKey)}
            color="info"
            size="small"
            isCompact={isCompact}
          />
        )}
        {presentation.kind === 'unavailable' && (
          <Chip
            label={t(presentation.messageKey)}
            color="warning"
            size="small"
            isCompact={isCompact}
          />
        )}
        {presentation.kind === 'booking' && (
          <>
            <Chip
              color={bookingStateColor(
                presentation.booking.state,
                presentation.booking.providerStatus,
                RUTERO_SALE_ORDER_BOOKING_STATE_COLORS,
              )}
              label={t(presentation.bookingStateLabelKey)}
              size="small"
              isCompact={isCompact}
            />
            {presentation.showUnknownProviderWarning && (
              <Chip
                color="warning"
                label={t('travelServices.booking.pendingChip')}
                size="small"
                isCompact={isCompact}
              />
            )}
            {presentation.showActiveOperation && (
              <ActiveOperationChip booking={presentation.booking} />
            )}
            {presentation.showProviderStatus && (
              <Chip
                variant="outlined"
                label={presentation.booking.providerStatus}
                size="small"
                isCompact={isCompact}
              />
            )}
          </>
        )}
      </Stack>

      {presentation.kind === 'booking' && (
        <>
          {(presentation.booking.provider ||
            presentation.booking.providerReference) && (
            <Typography variant="caption" color="text.secondary">
              {[
                presentation.booking.provider
                  ? `${t('rutero.connectors.provider')}: ${presentation.booking.provider}`
                  : undefined,
                presentation.booking.providerReference
                  ? `Ref: ${presentation.booking.providerReference}`
                  : undefined,
              ]
                .filter(Boolean)
                .join(' / ')}
            </Typography>
          )}
          {presentation.showHoldExpiry && (
            <Typography variant="caption" color="warning.main">
              {t('travelServices.booking.expiresAt', {
                date: new Date(
                  presentation.booking.expiresAt as string,
                ).toLocaleString(),
              })}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
}
